
import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';
// Default accounting settings
const defaultAccountingSettings = {
    fiscalYear: {
        startMonth: 1,
        startDay: 1,
        endMonth: 12,
        endDay: 31
    },
    accountingMethod: 'accrual', // or 'cash'
    vatReportingPeriod: 'quarterly', // or 'monthly', 'annually'
    accountPlans: {
        selectedPlan: 'BAS2023',
        customizedAccounts: {}
    },
    journalEntrySettings: {
        requireDescription: true,
        requireDocuments: false,
        allowFutureDated: true,
        requireApproval: false
    }
};
const AccountingSettingsContext = createContext({
    accountingSettings: defaultAccountingSettings,
    isLoading: false,
    updateAccountingSetting: () => { },
    saveAccountingSettings: () => { }
});
export const AccountingSettingsProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [accountingSettings, setAccountingSettings] = useState(defaultAccountingSettings);
    const [isLoading, setIsLoading] = useState(false);
    const fetchAccountingSettings = async () => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/settings/bulk`);
            const { data } = await axios.post(
                url,
                { keys: ['accounting_settings'] },
                { withCredentials: true }
            );
            if (data?.settings?.accounting_settings) {
                let parsedSettings;
                try {
                    if (typeof data.settings.accounting_settings === 'string') {
                        parsedSettings = JSON.parse(data.settings.accounting_settings);
                    } else {
                        parsedSettings = data.settings.accounting_settings;
                    }
                    setAccountingSettings({
                        ...defaultAccountingSettings,
                        ...parsedSettings
                    });
                } catch (parseError) {
                    console.error('Error parsing accounting_settings JSON:', parseError);
                }
            }
        } catch (error) {
            console.error('Error fetching accounting settings:', error);
        } finally {
            setIsLoading(false);
        }
    };
    // Register this context's refresh function with the parent
    useEffect(() => {
        const unregister = registerRefreshFunction('accountingSettings', fetchAccountingSettings);
        return unregister;
    }, [companyId]);
    // Update a section of accounting settings
    const updateAccountingSetting = (section, sectionData) => {
        setAccountingSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                ...sectionData
            }
        }));
    };
    // Save accounting settings to the server
    const saveAccountingSettings = async (section) => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/settings/bulk/update`);
            const dataToSave = section
                ? { [section]: accountingSettings[section] }
                : accountingSettings;
            const updates = [
                { key: 'accounting_settings', value: dataToSave }
            ];
            const { data } = await axios.post(
                url,
                { updates },
                { withCredentials: true }
            );
            return data.success;
        } catch (error) {
            console.error('Error saving accounting settings:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <AccountingSettingsContext.Provider value={{
            accountingSettings,
            isLoading,
            updateAccountingSetting,
            saveAccountingSettings
        }}>
            {children}
        </AccountingSettingsContext.Provider>
    );
};
export const useAccountingSettings = () => useContext(AccountingSettingsContext);
export default AccountingSettingsContext;
